import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp, Database, Activity, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatisticsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/statistics`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIOTypeColor = (type) => {
    const colors = {
      'DISINFORMATION': 'bg-red-500',
      'DEMORALIZATION': 'bg-orange-500',
      'DISCREDITATION': 'bg-yellow-500',
      'INTIMIDATION': 'bg-purple-500',
      'HATE_INCITEMENT': 'bg-pink-500',
      'PANIC_CREATION': 'bg-indigo-500',
      'PROVOCATION': 'bg-blue-500',
      'AUTHORITY_UNDERSCORE': 'bg-cyan-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getIOTypeNameRu = (type) => {
    const names = {
      'DISINFORMATION': 'Дезинформация',
      'DEMORALIZATION': 'Деморализация',
      'DISCREDITATION': 'Дискредитация',
      'INTIMIDATION': 'Запугивание',
      'HATE_INCITEMENT': 'Разжигание ненависти',
      'PANIC_CREATION': 'Создание паники',
      'PROVOCATION': 'Провокация',
      'AUTHORITY_UNDERSCORE': 'Подрыв доверия'
    };
    return names[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Загрузка статистики...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Статистика не доступна</p>
      </div>
    );
  }

  const totalIO = Object.values(stats.io_types_distribution || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3" data-testid="statistics-title">
            Статистика и аналитика
          </h1>
          <p className="text-lg text-gray-600">
            Общая статистика по анализам и обнаруженным ИО
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="stat-analyses">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-3xl font-bold text-gray-900">{stats.total_analyses}</span>
            </div>
            <h3 className="text-gray-600 font-medium">Всего анализов</h3>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="stat-terms">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-3xl font-bold text-gray-900">{stats.total_military_terms}</span>
            </div>
            <h3 className="text-gray-600 font-medium">Военных терминов</h3>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="stat-posts">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-3xl font-bold text-gray-900">{stats.total_social_posts}</span>
            </div>
            <h3 className="text-gray-600 font-medium">Постов из соцсетей</h3>
          </div>
        </div>

        {/* IO Types Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Распределение по типам ИО
          </h2>
          
          {Object.keys(stats.io_types_distribution || {}).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(stats.io_types_distribution).map(([type, count]) => {
                const percentage = totalIO > 0 ? (count / totalIO * 100).toFixed(1) : 0;
                return (
                  <div key={type} className="space-y-2" data-testid={`io-stat-${type}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{getIOTypeNameRu(type)}</span>
                      <span className="text-gray-600">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full ${getIOTypeColor(type)} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Данные о типах ИО отсутствуют
            </p>
          )}
        </div>

        {/* Recent Analyses */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Последние анализы
          </h2>
          
          {stats.recent_analyses && stats.recent_analyses.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_analyses.map((analysis, index) => (
                <div
                  key={analysis.id || index}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  data-testid={`recent-analysis-${index}`}
                >
                  <p className="text-gray-700 mb-2">{analysis.text}</p>
                  <span className="text-xs text-gray-500">
                    {analysis.created_at ? new Date(analysis.created_at).toLocaleString('ru-RU') : 'Неизвестно'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Последние анализы отсутствуют
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage;